import { Button, Menu } from '@mantine/core';
import { IconFilePlus } from '@tabler/icons-react';
import { useEffect, useRef } from 'react';
import { useSharepoint } from 'src/data-capture/file-management/drop-zone/useSharepoint';
import { useSharePointPicker } from 'src/data-capture/file-management/drop-zone/SharePointProvider';
import { OneDriveFileUploadInput } from 'src/data-capture/hooks/file-uploads/useOneDriveFileUpload';
import { PickedItem } from 'src/data-capture/file-management/drop-zone/SharePointButton';
import classes from './UploadPillButton.module.css';
type UploadPillButtonProps = {
  loading?: boolean;
  onFilesSelected: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onOneDriveFilesSelected: (input: OneDriveFileUploadInput) => unknown;
  label?: string;
  multiple?: boolean;
  accept?: string;
  disabled?: boolean;
  /** File extensions to filter in SharePoint picker (e.g., ['.pdf', '.docx']). */
  fileExtensions?: string[];
};

export function UploadPillButton({
  loading,
  onFilesSelected,
  onOneDriveFilesSelected,
  label = 'Upload documents or recordings',
  multiple = true,
  accept,
  disabled,
  fileExtensions,
}: UploadPillButtonProps) {
  const { pendingPickedItems, clearPendingPickedItems } = useSharePointPicker();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle items picked after a login redirect
  useEffect(() => {
    if (pendingPickedItems && pendingPickedItems.length > 0) {
      onOneDriveFilesSelected({
        items: pendingPickedItems,
        isResolved: true,
      });
      clearPendingPickedItems();
    }
  }, [pendingPickedItems, onOneDriveFilesSelected, clearPendingPickedItems]);

  const { handleClick } = useSharepoint({
    onPickedItems: async (items: PickedItem[]) => {
      await onOneDriveFilesSelected({
        items,
        isResolved: true,
      });
    },
    onAccountChange: () => {},
    switchAccountRequested: false,
    onSwitchAccountHandled: () => {},
    fileExtensions,
  });
  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        multiple={multiple}
        accept={accept}
        style={{ display: 'none' }}
        onChange={onFilesSelected}
      />
      <Menu shadow="md" disabled={disabled}>
        <Menu.Target>
          <Button
            component="label"
            radius="xl"
            leftSection={<IconFilePlus size={24} />}
            loading={loading}
            disabled={disabled}
            loaderProps={{ color: 'var(--mantine-color-brand-6)' }}
            classNames={{
              root: classes.root,
              label: classes.label,
              section: classes.section,
            }}
          >
            {label}
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Label>Source</Menu.Label>
          <Menu.Item onClick={() => fileInputRef.current?.click()}>
            Upload from your computer
          </Menu.Item>
          <Menu.Item onClick={handleClick}>Upload from SharePoint</Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </>
  );
}
